import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

export interface ApiError {
  message: string;
  status: number;
  data?: any;
}

class ApiClient {
  private client: AxiosInstance;
  private getAccessToken: () => string | null;
  private handleAuthError: (error: ApiError) => Promise<string | null>;
  private handleNationBuilderTokenError: (error: ApiError) => Promise<void>;

  constructor(
    baseURL?: string,
    authConfig?: {
      getAccessToken: () => string | null;
      handleAuthError: (error: ApiError) => Promise<string | null>;
      handleNationBuilderTokenError: (error: ApiError) => Promise<void>;
    },
  ) {
    const apiBaseURL =
      process.env.REACT_APP_API_URL || "https://api.globalromania.org";
    // const apiBaseURL = "http://127.0.0.1:8000"; // TODO remove

    if (!process.env.REACT_APP_API_URL) {
      console.warn("API URL not configured, using default");
    }

    this.getAccessToken = authConfig?.getAccessToken || (() => null);
    this.handleAuthError =
      authConfig?.handleAuthError || (() => Promise.resolve(null));
    this.handleNationBuilderTokenError =
      authConfig?.handleNationBuilderTokenError || (() => Promise.resolve());

    this.client = axios.create({
      baseURL: apiBaseURL,
      timeout: 900000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const accessToken = this.getAccessToken();

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const apiError: ApiError = {
          message:
            error.response?.data?.message ||
            error.message ||
            "An error occurred",
          status: error.response?.status || 500,
          data: error.response?.data,
        };
        if (apiError.data?.error_class === "AUTH_ERROR") {
          const newAccessToken = await this.handleAuthError(apiError);
          if (newAccessToken) {
            this.getAccessToken = () => newAccessToken;
            return Promise.reject({ retry_request: true });
          }
        } else if (apiError.data?.error_class === "NATIONBUILDER_TOKEN_ERROR") {
          await this.handleNationBuilderTokenError(apiError);
        }
        return Promise.reject(apiError);
      },
    );
  }

  async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.client.get(url, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      if (error && (error as any).retry_request) {
        return this.get(url, config);
      }
      throw error;
    }
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.client.post(
        url,
        data,
        config,
      );
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      if (error && (error as any).retry_request) {
        return this.post(url, data, config);
      }
      throw error;
    }
  }

  async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.client.put(
        url,
        data,
        config,
      );
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      if (error && (error as any).retry_request) {
        return this.put(url, data, config);
      }
      throw error;
    }
  }

  async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.client.delete(url, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      if (error && (error as any).retry_request) {
        return this.delete(url, config);
      }
      throw error;
    }
  }
}

export default ApiClient;
